import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { DownloadIcon, ExternalLinkIcon, PlusIcon, RotateCcwIcon, Trash2Icon, UploadIcon, AlertTriangleIcon } from 'lucide-react';
import { usePlatform } from '../../contexts/PlatformContext';
import { MenuEditor } from './MenuEditor';
import { PartnerProfileEditor } from './PartnerProfileEditor';
type View = 'sortiment' | 'profil';

/** Pick a partner on the left, manage their assortment or profile on the right. */
export function PartnerWorkspace() {
  const platform = usePlatform();
  const {
    partners
  } = platform;
  const [selected, setSelected] = useState(partners[0]?.place.slug ?? '');
  const [view, setView] = useState<View>('sortiment');
  const importRef = useRef<HTMLInputElement>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const partner = partners.find(p => p.place.slug === selected) ?? partners[0];
  const exportData = () => {
    const blob = new Blob([platform.exportJson()], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `smaken-plattform-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const importData = async (file: File | undefined) => {
    if (!file) return;
    const ok = platform.importJson(await file.text());
    setNotice(ok ? 'Importen är klar.' : 'Filen kunde inte läsas — är det en export från Småken?');
  };
  return <div data-id="element-218" data-mp="398t0s6q71iop">
      <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-219" data-mp="3992vg7m6oe0q">
        <div data-id="element-220" data-mp="3mzfzhyvyf8ks">
          <h1 className="font-display text-3xl font-medium text-gray-900" data-id="element-221" data-mp="3n07tx6dtf09h">Verksamheter</h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-222" data-mp="3i7esardn881m">
            Allt du sparar syns direkt på sajten. Senast ändrat {new Date(platform.updatedAt).toLocaleString('sv-SE', {
            dateStyle: 'short',
            timeStyle: 'short'
          })}.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 text-sm" data-id="element-223" data-mp="2phpok69jttwb">
          <button onClick={exportData} className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 font-medium text-gray-700 hover:bg-gray-50" data-id="element-224" data-mp="tfmc366ksk9a">
            <DownloadIcon className="h-4 w-4" data-id="element-225" data-mp="136p8vvsh2d2r" /> Exportera
          </button>
          <button onClick={() => importRef.current?.click()} className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 font-medium text-gray-700 hover:bg-gray-50" data-id="element-226" data-mp="138teqj9xlm5o">
            <UploadIcon className="h-4 w-4" data-id="element-227" data-mp="ak4k933dsf2w" /> Importera
          </button>
          <button onClick={() => window.confirm('Återställ allt till originalsortimentet? Dina ändringar försvinner.') && platform.resetPlatform()} className="flex items-center gap-2 rounded-full px-4 py-2 font-medium text-gray-500 hover:bg-gray-100" data-id="element-228" data-mp="fhegb1l9zozp">
            <RotateCcwIcon className="h-4 w-4" data-id="element-229" data-mp="1vucsp8lx4zoc" /> Återställ
          </button>
          <input ref={importRef} type="file" accept="application/json" className="hidden" onChange={e => {
          importData(e.target.files?.[0]);
          e.target.value = '';
        }} data-id="element-230" data-mp="1zvybtbp1qa6z" />
        </div>
      </div>

      {(platform.storageError || notice) && <p className={`mt-4 flex items-center gap-2 rounded-2xl px-4 py-3 text-sm ${platform.storageError ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-231" data-mp="3gdyntob0j3s8">
          {platform.storageError && <AlertTriangleIcon className="h-4 w-4 shrink-0" data-id="element-232" data-mp="36n3rts7ysptl" />}
          {platform.storageError ?? notice}
        </p>}

      <div className="mt-8 grid gap-8 lg:grid-cols-[16rem,minmax(0,1fr)]" data-id="element-233" data-mp="31lwllxlgzyp1">
        {/* Partner list */}
        <nav aria-label="Verksamheter" data-id="element-234" data-mp="2snkw5ywcb6y6">
          <ul className="space-y-1" data-id="element-235" data-mp="2som5nyxpjlds">
            {partners.map(p => {
            const active = p.place.slug === partner?.place.slug;
            return <li key={p.place.slug} data-id="element-236" data-mp="qvy21oh5ivxh">
                  <button onClick={() => setSelected(p.place.slug)} className={`flex w-full items-center gap-3 rounded-2xl p-2 text-left transition-colors ${active ? 'bg-black text-white' : 'hover:bg-white'}`} data-id="element-237" data-mp="quwqzk04y9fr">
                    <span className="h-10 w-10 shrink-0 overflow-hidden rounded-xl bg-gray-200" data-id="element-238" data-mp="3jr7yxtkce2cu">
                      {p.place.image && <img src={p.place.image} alt="" className="h-full w-full object-cover" data-id="element-239" data-mp="35pvlbosnmcu4" />}
                    </span>
                    <span className="min-w-0 flex-1" data-id="element-240" data-mp="3aogljo7wowik">
                      <span className="block truncate text-sm font-medium" data-id="element-241" data-mp="2w0lsu7twes5h">{p.place.name}</span>
                      <span className={`block text-xs ${active ? 'text-white/60' : 'text-gray-500'}`} data-id="element-242" data-mp="30sdkklosj3x7">
                        {p.items.length} produkter · {p.published ? 'Publicerad' : 'Utkast'}
                      </span>
                    </span>
                  </button>
                </li>;
          })}
          </ul>
          <button onClick={() => {
          setSelected(platform.addPartner());
          setView('profil');
        }} className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-gray-200 py-3 text-sm font-medium text-gray-600 transition-colors hover:border-gray-400" data-id="element-243" data-mp="2d2rmy2o2gwrb">
            <PlusIcon className="h-4 w-4" data-id="element-244" data-mp="28e9sblatxb6k" /> Ny verksamhet
          </button>
        </nav>

        {/* Detail */}
        {partner ? <div className="min-w-0" data-id="element-245" data-mp="23d2jrk0v47yu">
            <div className="flex flex-wrap items-center justify-between gap-4 rounded-3xl bg-white p-5" data-id="element-246" data-mp="2heezbuc427k7">
              <div className="min-w-0" data-id="element-247" data-mp="2cfbsc4cklr9l">
                <p className="truncate font-display text-2xl font-medium text-gray-900" data-id="element-248" data-mp="2ceae5r09conn">{partner.place.name}</p>
                <p className="text-sm text-gray-500" data-id="element-249" data-mp="2r3os7ces3u9d">{partner.place.city} · {partner.sections.length} menydelar</p>
              </div>
              <div className="flex flex-wrap items-center gap-2" data-id="element-250" data-mp="2m7hl3ganccpu">
                <label className="flex cursor-pointer items-center gap-2 rounded-full bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700" data-id="element-251" data-mp="305fu06677gxy">
                  <input type="checkbox" checked={partner.published} onChange={e => platform.setPublished(partner.place.slug, e.target.checked)} className="h-4 w-4 accent-black" data-id="element-252" data-mp="336rem0emy1xa" />
                  Publicerad
                </label>
                <Link to={`/plats/${partner.place.slug}`} target="_blank" className="flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100" data-id="element-253" data-mp="3hwgteyfni4g1">
                  <ExternalLinkIcon className="h-4 w-4" data-id="element-254" data-mp="3qvalqcly88ns" /> Visa
                </Link>
                <button aria-label="Ta bort verksamhet" onClick={() => window.confirm(`Ta bort ${partner.place.name} och hela sortimentet?`) && platform.deletePartner(partner.place.slug)} className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-red-50 hover:text-red-600" data-id="element-255" data-mp="3vun8yygqfhqw">
                  <Trash2Icon className="h-4 w-4" data-id="element-256" data-mp="26i3923k3w74r" />
                </button>
              </div>
            </div>

            <div className="mt-6 flex gap-1 border-b border-gray-200" data-id="element-257" data-mp="iunsla6cctlw">
              {(['sortiment', 'profil'] as View[]).map(v => <button key={v} onClick={() => setView(v)} className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${view === v ? 'border-black text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-900'}`} data-id="element-258" data-mp="etlrensv67ha">
                  {v === 'sortiment' ? `Sortiment (${partner.items.length})` : 'Profil'}
                </button>)}
            </div>

            <div className="mt-6" data-id="element-259" data-mp="sjfhf9g3uxsl">
              {view === 'sortiment' ? <MenuEditor partner={partner} data-id="element-260" data-mp="3i9suot7da2pj" /> : <PartnerProfileEditor place={partner.place} onSave={place => setSelected(platform.updatePartnerProfile(partner.place.slug, place))} data-id="element-261" data-mp="3dafw5b74v774" />}
            </div>
          </div> : <p className="rounded-3xl bg-white p-10 text-center text-gray-500" data-id="element-262" data-mp="1m7z537u56k3z">Inga verksamheter ännu.</p>}
      </div>
    </div>;
}